import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { IActiveId } from "@/lib/widgetsLayoutDir";
import bondSecuritiesStore from "@/store/bondSecuritiesStore";
import portfoliosStore from "@/store/portfoliosStore";
import tradeFromScratchStore, {
  DEFAULT_TICKET_INSTRUMENT,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";
import BondOptionTicket from "./BondOptionTicket";
import BondTicket from "./BondTicket";
import FutureTicket from "./FutureTicket";
import RepoTicket from "./RepoTicket";
import SwapTicket from "./SwapTicket";
import SwaptionTicket from "./SwaptionTicket";
import {
  ReferenceDataGate,
  loadTradeFromScratchReferenceData,
  prefetchSwapReferenceDataWhenIdle,
  returnReferenceDataGate,
  returnReferenceDataMessage,
} from "./helper/tradeFromScratchHelper";
import { useTradeDateRollover } from "./helper/useTradeDateRollover";

// LQC-2254 - Trade From Scratch widget entry point. Fetches the portfolios and bond
// securities it needs on mount (only this widget pulls the portfolios, and only it and
// the Prices widget pull the large bonds payload), then renders the bond ticket once
// BOTH have resolved. See README.md for the epic state.
// v1 is a single BND outright. Structures, multi-portfolio allocations and the other
// instruments are open questions in the README.
interface ITradeFromScratchIndex {
  cryptoID?: string;
  loadedFromPopout?: boolean;
  activeId?: Partial<IActiveId>;
}

// Falls back only where the widget is rendered outside a grid (tests). In the app
// GenerateAllWidgets always passes the layout id.
export const FALLBACK_TICKET_KEY = "TradeFromScratch";

// Bond is the default, so anything unrecognised falls through to it.
function returnTicketForInstrument(
  instrument: TicketInstrument,
  ticketKey: string,
) {
  if (instrument === TicketInstrument.Future) {
    return <FutureTicket ticketId={ticketKey} />;
  }
  if (instrument === TicketInstrument.BondOption) {
    return <BondOptionTicket ticketId={ticketKey} />;
  }
  if (instrument === TicketInstrument.Swap) {
    return <SwapTicket ticketId={ticketKey} />;
  }
  if (instrument === TicketInstrument.Swaption) {
    return <SwaptionTicket ticketId={ticketKey} />;
  }
  if (instrument === TicketInstrument.Repo) {
    return <RepoTicket ticketId={ticketKey} />;
  }
  return <BondTicket ticketId={ticketKey} />;
}

const TradeFromScratchIndex = ({ cryptoID }: ITradeFromScratchIndex) => {
  const { getAccessTokenSilently } = useAuth0();

  const portfoliosStatus = portfoliosStore((state) => state.portfoliosStatus);
  const bondSecuritiesStatus = bondSecuritiesStore(
    (state) => state.bondSecuritiesStatus,
  );
  const ticketKey = cryptoID || FALLBACK_TICKET_KEY;
  // LQC-2493 - which ticket the widget shows. Futures reference data is NOT part of
  // the gate below: the future ticket fetches it when it mounts.
  const instrument = tradeFromScratchStore(
    (state) => state.instrumentByTicketId[ticketKey] ?? DEFAULT_TICKET_INSTRUMENT,
  );

  const gate = returnReferenceDataGate(portfoliosStatus, bondSecuritiesStatus);

  useEffect(() => {
    loadTradeFromScratchReferenceData(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps open tickets on today's trade date across midnight (LQC-2981).
  useTradeDateRollover();

  // Swap lists are optional extras, so they wait for idle rather than race the gate.
  useEffect(() => {
    return prefetchSwapReferenceDataWhenIdle(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gate !== ReferenceDataGate.Ready) {
    const isFailed = gate === ReferenceDataGate.Failed;
    return (
      <div
        data-testid="TradeFromScratchContainer"
        id="TradeFromScratchContainer"
        className="flex h-full w-full flex-col items-center justify-center gap-2 text-sm dark:text-white"
      >
        <div
          data-testid="TradeFromScratchReferenceDataMessage"
          id="TradeFromScratchReferenceDataMessage"
          className="text-center opacity-60"
        >
          {returnReferenceDataMessage(gate)}
        </div>
        {isFailed && (
          <button
            data-testid="TradeFromScratchRetryButton"
            id="TradeFromScratchRetryButton"
            className="rounded-full border border-gray-400 px-3 py-1 text-xs"
            onClick={() =>
              loadTradeFromScratchReferenceData(getAccessTokenSilently, true)
            }
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // The widget shell already supplies padding and the scroll container, so this only
  // fills the space it is given.
  return (
    <div
      data-testid="TradeFromScratchContainer"
      id="TradeFromScratchContainer"
      className="h-full w-full dark:text-white"
    >
      {returnTicketForInstrument(instrument, ticketKey)}
    </div>
  );
};

export default TradeFromScratchIndex;
